import { useEffect, useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from 'services/context';
import { useTranslation } from 'services/hooks';
import Icon from 'components/Icon';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Input/Select';
import './style.css';

const UserPreferences = () => {
	const { Api, isLoading, user } = useAppContext();
	const [categoriesList, setCategoriesList] = useState([]);
	const [userPreferences, setUserPreferences] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState({});
	const [selectedSubCategory, setSelectedSubCategory] = useState({});
	const [isAddingPreference, setIsAddingPreference] = useState(false);
	const [validationError, setValidationError] = useState(false);

	const [newItemId, setNewItemId] = useState(null); // Track the new item ID for animation

	const otherInputIds = [52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63]; //All "Other" possible options of the flow
	const otherCategoriesId = [12];

	const selectOtherCategory = (selectedCategory && otherCategoriesId.includes(parseInt(selectedCategory.value))) || false;
	const selectOtherSubCategory = (selectedSubCategory && otherInputIds.includes(parseInt(selectedSubCategory.value))) || false;

	const formRef = useRef();
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'personal_area';

	const hasMaxPrefrences = useMemo(() => {
		return userPreferences && userPreferences.length >= 5;
	}, [userPreferences]);

	//arrange data and remove the language key
	const categoryOptions = useMemo(() => {
		return (
			categoriesList.map(cat => {
				return {
					CategoryId: cat.CategoryId,
					CategoryName: cat.CategoryNames['EN'],
					Subcategories: cat.Subcategories.map(sub => {
						return {
							SubcategoryId: sub.SubcategoryId,
							SubcategoryName: sub.SubcategoryNames['EN'],
						};
					}),
				};
			}) || []
		);
	}, [categoriesList]);

	//extract categories as DDL options
	const categoryNames = useMemo(() => {
		return (
			categoryOptions.map(cat => {
				return {
					value: cat.CategoryId,
					description: cat.CategoryName,
				};
			}) || []
		);
	}, [categoriesList]);

	//extract the subCategories by matching category as DDL options
	const subCategoryNames = useMemo(() => {
		if (!Object.keys(selectedCategory).length > 0) return;
		const matchSubcategories = categoryOptions.find(cat => cat.CategoryId === selectedCategory.value);
		if (matchSubcategories)
			return matchSubcategories.Subcategories.map(sub => {
				const isOther = otherInputIds.includes(sub.SubcategoryId);
				const alreadySelected = userPreferences.some(pref => sub.SubcategoryId === pref.subCategoryId);
				return {
					value: sub.SubcategoryId,
					description: sub.SubcategoryName,
					disabled: alreadySelected && !isOther,
				};
			});
		return [];
	}, [categoriesList, selectedCategory, userPreferences]);

	const getCategoriesList = async () => {
		const _categoriesList = await Api.GetLpCategories();
		if (_categoriesList && _categoriesList.length > 0) setCategoriesList(_categoriesList);
	};

	//fetch the user saved categories and DDLS data
	useEffect(() => {
		(async () => {
			const _userPreferences = await Api.GetCategoriesByEmail();
			if (_userPreferences && _userPreferences.length > 0) setUserPreferences(_userPreferences);
			await getCategoriesList();
		})();
	}, []);

	//handle "Add Preferences" button click
	const addPreferenceOnClick = async () => {
		setSelectedCategory({});
		setSelectedSubCategory({});
		formRef && formRef?.current?.reset();
		setIsAddingPreference(true);
	};

	//handle remove preference onClick
	const removePreference = async preferenceRecordId => {
		const isRemoved = await Api.DeleteUserCategory(preferenceRecordId);
		if (isRemoved) {
			const updatedPrefrences = userPreferences.filter(cat => cat.recordId !== preferenceRecordId);
			setUserPreferences(updatedPrefrences);
		}
	};

	//protects long strings to avoid layout break, returns "[string]..." for long strings
	const subStringCategories = string => {
		return string.length > 30 ? string.substring(0, 30) + '...' : string;
	};

	//handle select a category
	const getSelectedCategory = e => {
		e.preventDefault();
		setValidationError(false);
		const form = new FormData(formRef.current);
		const formData = Object.fromEntries(form.entries());
		if (formData.category === '') {
			setValidationError(true);
			return;
		}
		let PreferenceObject = null;
		if (!Object.keys(selectedCategory).length) {
			PreferenceObject = categoryNames.find(cat => cat.value === parseInt(formData.category));
			if (Object.keys(PreferenceObject).length > 0) setSelectedCategory(PreferenceObject);
		}
	};

	//insert user preference
	const saveUserPreference = async e => {
		e.preventDefault();
		setValidationError(false);
		const form = new FormData(formRef.current);
		const formData = Object.fromEntries(form.entries());
		let preferenceObject = null;
		let choosedSubCategoryId = null;
		if (!formData.sub_category && !formData.free_text) {
			setValidationError(true);
			return;
		}
		if (selectedCategory.value && formData.sub_category) {
			preferenceObject = subCategoryNames.find(cat => cat.value === parseInt(formData.sub_category));
			if (Object.keys(preferenceObject).length > 0) setSelectedSubCategory(preferenceObject);
			choosedSubCategoryId = preferenceObject.value;
			if (otherInputIds.includes(choosedSubCategoryId)) return;
		} else if (selectOtherCategory || selectOtherSubCategory) {
			choosedSubCategoryId = subCategoryNames && subCategoryNames[subCategoryNames.length - 1].value; //other is always the last on the list
		}
		try {
			const _email = user.userName;
			const res = await Api.InsertUserCategory(choosedSubCategoryId, formData.free_text ?? '', _email);
			if (res) {
				setUserPreferences(prev => [...prev, res]);
				setIsAddingPreference(false);
				setNewItemId(res.recordId);
			}
		} catch (error) {
			console.warn(error);
		}
	};

	//renders the category component by the situation
	const renderReleventCategoryComponent = useMemo(() => {
		if (selectedCategory && selectedCategory.description) {
			return (
				<div className="preference__categories_wrapper__categoryName">
					{subStringCategories(selectedCategory.description)}
					{selectedCategory.description && <span> &gt; </span>}
				</div>
			);
		} else {
			return (
				<>
					<Select
						options={categoryNames}
						placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_category_field`)}
						name="category"
						sortKey="value"
					/>
					<Button
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_category_button`)}
						small
						extraClassName="next"
						onClick={getSelectedCategory}
					/>
				</>
			);
		}
	}, [selectedCategory, selectOtherCategory, categoryOptions, getTranslation]);

	//renders the subCategory component by the situation
	const renderReleventSubCategoryComponent = useMemo(() => {
		if (Object.keys(selectedCategory).length > 0) {
			if (selectOtherCategory || selectOtherSubCategory) {
				return (
					<>
						<Input
							name="free_text"
							placeHolder={`${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_sub_category_type`)} ${
								selectOtherCategory
									? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_other_category_field`)
									: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_sub_category_field`)
							} ${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_sub_category_name`)}`}
						/>
						<Button
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_sub_category_button`)}
							small
							extraClassName="save"
							onClick={saveUserPreference}
						/>
					</>
				);
			} else if (selectedSubCategory && selectedSubCategory.description) {
				return (
					<div className="preference__categories_wrapper__subCategoryName">
						{subStringCategories(selectedSubCategory.description)}
						{selectedCategory.description && <span> &gt; </span>}
					</div>
				);
			} else {
				return (
					<>
						<Select
							options={subCategoryNames}
							sortKey="value"
							placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_sub_category_placeholder`)}
							name="sub_category"
						/>
						<Button
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_sub_category_button`)}
							small
							extraClassName="save"
							onClick={saveUserPreference}
						/>
					</>
				);
			}
		}
	}, [selectedCategory, selectedSubCategory, selectOtherSubCategory, categoryOptions, getTranslation]);

	///Framer motion animation varients
	const containerVariants = {
		initial: { opacity: 0 }, // Start with the container hidden
		animate: {
			opacity: 1,
			transition: {
				staggerChildren: 0.2, // Delay between child animations
				when: 'beforeChildren', // Ensure children animate after the parent becomes visible
			},
		},
	};

	const itemVariants = {
		initial: {
			opacity: 0,
			x: 150, // Exit to the right
			y: 0,
		}, // Start hidden
		animate: index => ({
			opacity: 1,
			x: 0,
			transition: {
				delay: newItemId ? 0 : index * 0.08, // Delay based on the index
				duration: 0.2,
			},
		}),
		exit: {
			opacity: 0,
			x: 150, // Exit to the right
			transition: { duration: 0.2 },
		},
	};

	const dropdownVariants = {
		hidden: { opacity: 0, y: 20 }, // Initial animation: Fade in and move up
		visible: { opacity: 1, y: 0 }, // Final visible state
		shake: {
			opacity: 1,
			y: 0, // Fix the vertical position
			x: [0, -10, 10, -8, 8, -5, 5, 0], // Shake horizontally
			transition: { duration: 0.5 },
		},
	};

	return (
		<div className="UserPreferences">
			<div className="UserPreferences__title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_title`)}</div>
			<div className="preferences_container">
				<AnimatePresence>
					{!isAddingPreference && !userPreferences.length && (
						<div className="preferences_container__title">
							{isLoading
								? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_container_loading_title`)
								: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_container_title`)}
						</div>
					)}
					{userPreferences &&
						userPreferences.length > 0 &&
						userPreferences.map((pref, index) => {
							return (
								<motion.div
									variants={containerVariants} // Attach variants to the container
									initial="initial"
									animate="animate"
									exit="exit"
									className="preference"
									key={pref.recordId}
								>
									<motion.div
										variants={itemVariants}
										className="preference__categories_wrapper"
										custom={index}
										onAnimationComplete={() => {
											if (pref.recordId === newItemId) setNewItemId(null); // Reset new item ID after animation
										}}
									>
										<div className="preference__categories_wrapper__categoryName" title={pref.categoryName}>
											{pref.categoryName}
											{/* {subStringCategories(pref.categoryName) + ` > `} */}
										</div>
										{pref.categoryName && <span> &gt; </span>}
										<div
											className="preference__categories_wrapper__subCategoryName"
											title={pref.freeText === '' ? pref.subCategoryName : pref.freeText}
										>
											{subStringCategories(pref.freeText === '' ? pref.subCategoryName : pref.freeText)}
										</div>
									</motion.div>
									<div
										className="preference__remove"
										onClick={() => removePreference(pref.recordId)}
										title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_remove_button`)}
									>
										<Icon icon="removeIcon" size={16} />
									</div>
								</motion.div>
							);
						})}
					{isAddingPreference && !hasMaxPrefrences && (
						<form ref={formRef} className="preference__categories_wrapper">
							<motion.div
								key="category"
								initial="hidden"
								animate={validationError ? 'shake' : 'visible'}
								className="category"
								variants={dropdownVariants}
								onAnimationComplete={() => {
									// Reset invalid state to allow repeated shakes
									if (validationError) setValidationError(false);
								}}
							>
								{renderReleventCategoryComponent}
							</motion.div>

							{renderReleventSubCategoryComponent && (
								<motion.div
									key="sub_category"
									initial="hidden"
									animate={validationError ? 'shake' : 'visible'}
									className="sub_category"
									variants={dropdownVariants}
									onAnimationComplete={() => {
										// Reset invalid state to allow repeated shakes
										if (validationError) setValidationError(false);
									}}
								>
									{renderReleventSubCategoryComponent}
								</motion.div>
							)}
						</form>
					)}
				</AnimatePresence>

				<div className="preference__btn_container">
					<Button
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_preferences_add_button`)}
						small
						startIcon="addIcon"
						disabled={hasMaxPrefrences || isAddingPreference}
						onClick={addPreferenceOnClick}
					/>
				</div>
			</div>
		</div>
	);
};
export default UserPreferences;
